import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, Sparkles, Wallet, UserCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

// Admin: pick which user receives the PayPal transaction (regular → general accounting, extra → Contabilidad Extra)
export const AssignPayPalDialog = ({ tx, open, onOpenChange, onConfirm, busy }) => {
    const { user, getAuthHeaders } = useAuth();
    const [users, setUsers] = useState([]);
    const [filter, setFilter] = useState('');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!open) return;
        setSelected(user?.id || null);
        setFilter('');
        axios.get(`${API}/api/users`, { headers: getAuthHeaders() })
            .then((r) => setUsers((r.data || []).filter((u) => u.role !== 'supervisor').sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() => setUsers([]));
    }, [open, user, getAuthHeaders]);

    const list = users.filter((u) => !filter || u.name.toLowerCase().includes(filter.toLowerCase()) || (u.email || '').toLowerCase().includes(filter.toLowerCase()));
    const sel = users.find((u) => u.id === selected);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] sm:w-full max-h-[90vh] overflow-y-auto z-[70] p-4 sm:p-6" data-testid="paypal-assign-dialog">
                <DialogHeader>
                    <DialogTitle>Confirmar y asignar</DialogTitle>
                    <DialogDescription>
                        {tx?.sender_name} · neto <b>{money(tx?.net_amount)}</b>. Elige a qué usuario se contabiliza.
                        {tx?.on_hold && <span className="block mt-2 text-orange-700 font-medium" data-testid="assign-hold-warning">Pago en retención temporal de PayPal: confírmalo manualmente solo cuando PayPal lo haya liberado.</span>}
                    </DialogDescription>
                </DialogHeader>
                <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar usuario…" data-testid="paypal-assign-search" />
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="paypal-assign-list">
                    {list.map((u) => (
                        <button
                            key={u.id}
                            type="button"
                            onClick={() => setSelected(u.id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-sky-50 ${selected === u.id ? 'bg-sky-100' : ''}`}
                            data-testid={`paypal-assign-user-${u.id}`}
                        >
                            <UserCircle className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="flex-1 truncate">{u.name}{u.id === user?.id ? ' (yo)' : ''}</span>
                            {u.is_extra && <span className="text-[10px] bg-fuchsia-100 text-fuchsia-700 px-1.5 rounded inline-flex items-center gap-1"><Sparkles className="w-3 h-3" />Extra</span>}
                            {u.is_paypal && <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 rounded inline-flex items-center gap-1"><Wallet className="w-3 h-3" />PayPal</span>}
                        </button>
                    ))}
                    {list.length === 0 && <p className="px-3 py-4 text-sm text-slate-400 text-center">Sin resultados</p>}
                </div>
                <p className="text-xs text-slate-500" data-testid="paypal-assign-hint">
                    {sel?.is_extra ? 'Se contabilizará en Contabilidad Extra.' : 'Se contabilizará en la contabilidad general.'}
                </p>
                <Button
                    className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                    disabled={!selected || busy}
                    onClick={() => onConfirm(tx, selected)}
                    data-testid="paypal-assign-confirm-btn"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar {money(tx?.net_amount)} a {sel?.name || '…'}</>}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default AssignPayPalDialog;
